import { t } from "@lingui/core/macro";

export type OfferedCommitmentPlan = {
  id: number;
  periods: number;
  priceCents: number;
};

type CommitmentVariation = {
  requiresCommitment: boolean;
  commitmentPlans: OfferedCommitmentPlan[];
};

type SaleContext = { commitmentsEnabled: boolean; isCofunded: boolean };

/**
 * None means no picker. A one-time subscription bills a single period, so no term can
 * run; a company share absorbs part of the term's saving, so the two are not sold together.
 */
export function getOfferedCommitmentPlans(
  variation: CommitmentVariation | undefined,
  {
    commitmentsEnabled,
    isCofunded,
    isRecurring,
  }: SaleContext & { isRecurring: boolean },
): OfferedCommitmentPlan[] {
  if (variation == null || !commitmentsEnabled || !isRecurring || isCofunded) {
    return [];
  }
  return variation.commitmentPlans;
}

/** A commitment-only variation has nothing to fall back on once a company pays part. */
export function commitmentCofundingError(
  variation: CommitmentVariation | undefined,
  { commitmentsEnabled, isCofunded }: SaleContext,
): string | null {
  if (!isCofunded || !commitmentsEnabled || !variation?.requiresCommitment) {
    return null;
  }
  return t`Ta variacija se prodaja samo z vezavo, vezave pa ni mogoče skleniti, kadar del plačuje podjetje.`;
}

/** A commitment-only variation needs a plan, which a one-time sale cannot sign. */
export function canSellOneTime(
  variation: CommitmentVariation | undefined,
  context: SaleContext,
): boolean {
  if (!variation?.requiresCommitment) {
    return true;
  }
  return (
    getOfferedCommitmentPlans(variation, { ...context, isRecurring: true })
      .length === 0
  );
}

/** The editor skips its recurring check without the flag, so a stored variation can disagree. */
export function saleIsRecurring(
  variation: CommitmentVariation & { isRecurring: boolean },
  context: SaleContext,
): boolean {
  return variation.isRecurring || !canSellOneTime(variation, context);
}

/** A plan left over from another variation is not on offer, so it is not chosen. */
export function resolveChosenCommitmentPlan(
  planId: number | null,
  offeredPlans: OfferedCommitmentPlan[],
): OfferedCommitmentPlan | null {
  return offeredPlans.find((plan) => plan.id === planId) ?? null;
}

export function commitmentPlanChoiceError(
  variation: CommitmentVariation | undefined,
  chosenPlan: OfferedCommitmentPlan | null,
  offeredPlans: OfferedCommitmentPlan[],
): string | null {
  if (
    chosenPlan != null ||
    offeredPlans.length === 0 ||
    !variation?.requiresCommitment
  ) {
    return null;
  }
  return t`Ta variacija se prodaja samo z vezavo. Izberite vezavo.`;
}
